import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Injector,
  OnInit,
  signal,
} from '@angular/core';
import { DynamicItemBaseComponent } from '@ups/xplat/dynamic-render';
import {
  DYNAMIC_SUBLOCATION_SCOPE,
  SearchSublocationService,
  sublocationLoadFailedHint,
  sublocationScopeHint,
  sublocationSuggestions,
} from '@ups/tool-tracker';
import { takeUntil } from 'rxjs';
import { sublocationControlState } from './dynamic-typeahead-sublocation.state';

/**
 * WI 34981: web renderer for `typeahead-sublocation`. The control always holds a plain string,
 * so any text saves as typed. When the host app provides DYNAMIC_SUBLOCATION_SCOPE, that
 * warehouse's names are offered as suggestions and an unknown value warns; without it the
 * control is a plain text field.
 */
@Component({
  selector: 'ups-dynamic-typeahead-sublocation',
  templateUrl: 'dynamic-typeahead-sublocation.component.html',
  styleUrls: ['dynamic-typeahead-sublocation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // eslint-disable-next-line @angular-eslint/prefer-standalone -- pre-commit-allow: declared in DynamicRenderComponentsModule beside the non-standalone ups-dynamic-helper-label, as every sibling control is (WI 34981)
  standalone: false,
})
export class DynamicTypeaheadSublocationComponent
  extends DynamicItemBaseComponent
  implements OnInit
{
  private readonly scopeGetter = inject(DYNAMIC_SUBLOCATION_SCOPE, {
    optional: true,
  });
  private readonly injector = inject(Injector);

  private readonly names = signal<Array<string>>([]);
  /** The control's value as text. The autocomplete binds this, never the form control itself. */
  readonly text = signal('');
  /** What the user is typing; the suggestions follow it before the value commits. */
  private readonly query = signal('');

  readonly disabled = signal(false);
  readonly scopeHint = signal('');
  readonly state = computed(() =>
    sublocationControlState(this.names(), this.text())
  );
  readonly suggestions = computed(() =>
    sublocationSuggestions(this.names(), this.query())
  );

  override ngOnInit() {
    super.ngOnInit();
    const control = this.control;
    if (control) {
      this.syncValue(control.value);
      control.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => this.syncValue(value));
      control.statusChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.disabled.set(control.disabled));
    }
    if (!this.config?.isFormBuilder) {
      this.loadNames();
    }
  }

  onFilterChange(query: string) {
    this.query.set(query ?? '');
  }

  /** A pick, a commit of typed text, or the clear button: the answer is the plain string. */
  onValueChange(value: string) {
    const control = this.control;
    if (!control) {
      return;
    }
    control.setValue(value ?? '');
    control.markAsDirty();
  }

  onBlur() {
    this.control?.markAsTouched();
  }

  /**
   * Bound by value, not formControlName: a `{ location }` record written to the form control
   * reaches a bound Kendo AutoComplete before valueChanges fires, and it throws on a non-string.
   * Here the record only ever reaches this method, which keeps the control a plain string.
   */
  private syncValue(value: unknown) {
    const { text } = sublocationControlState([], value);
    this.text.set(text);
    this.query.set(text);
    this.disabled.set(!!this.control?.disabled);
    if (value != null && typeof value === 'object') {
      this.control?.setValue(text, { emitEvent: false });
    }
  }

  private get control() {
    const name = this.config?.formControlName;
    return name ? this.group?.get(name) : null;
  }

  private loadNames() {
    if (!this.scopeGetter) {
      return;
    }
    const scope = this.scopeGetter();
    this.scopeHint.set(sublocationScopeHint(scope));
    if (!scope) {
      return;
    }
    // Resolved only with a scope, so a host without the Tool Tracker API never builds its client.
    this.injector
      .get(SearchSublocationService)
      .namesFor(scope)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (names) => this.names.set(names),
        error: () => {
          this.names.set([]);
          this.scopeHint.set(sublocationLoadFailedHint(scope));
        },
      });
  }
}
